/**
 * KeyboardManager
 * Maps QWERTY keyboard keys to musical notes.
 */
export class KeyboardManager {
    constructor() {
        this.baseOctave = 3;
        this.activeKeys = new Map(); // key -> note
        
        // Mapping: KeyCode -> { note: string, offset: number (semitones from C) }
        this.keyMap = {
            'KeyA': 0,  // C
            'KeyW': 1,  // C#
            'KeyS': 2,  // D
            'KeyE': 3,  // D#
            'KeyD': 4,  // E
            'KeyF': 5,  // F
            'KeyT': 6,  // F#
            'KeyG': 7,  // G
            'KeyY': 8,  // G#
            'KeyH': 9,  // A
            'KeyU': 10, // A#
            'KeyJ': 11, // B
            'KeyK': 12, // C (Next Octave)
            'KeyO': 13, // C#
            'KeyL': 14, // D
            'KeyP': 15, // D#
            'Semicolon': 16, // E
            'Quote': 17, // F
        };

        this.onNoteOn = null;
        this.onNoteOff = null;
        this.onOctaveChange = null;

        window.addEventListener('keydown', (e) => this.handleKeyDown(e));
        window.addEventListener('keyup', (e) => this.handleKeyUp(e));
    }

    handleKeyDown(e) {
        if (e.repeat) return;
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        // Octave switching
        if (e.code === 'KeyZ') {
            const oldOctave = this.baseOctave;
            this.baseOctave = Math.max(0, this.baseOctave - 1);
            if (this.baseOctave !== oldOctave && this.onOctaveChange) {
                this.onOctaveChange(this.baseOctave);
            }
            return;
        }
        if (e.code === 'KeyX') {
            const oldOctave = this.baseOctave;
            this.baseOctave = Math.min(7, this.baseOctave + 1);
            if (this.baseOctave !== oldOctave && this.onOctaveChange) {
                this.onOctaveChange(this.baseOctave);
            }
            return;
        }

        const offset = this.keyMap[e.code];
        if (offset !== undefined) {
            const midiNote = (this.baseOctave + 1) * 12 + offset;
            const freq = 440 * Math.pow(2, (midiNote - 69) / 12);
            const noteName = this.getNoteName(midiNote);
            
            this.activeKeys.set(e.code, { midiNote, freq, noteName });
            
            if (this.onNoteOn) {
                this.onNoteOn(noteName, freq, 0.8, `kb-${e.code}`);
            }
        }
    }

    handleKeyUp(e) {
        const info = this.activeKeys.get(e.code);
        if (info) {
            this.activeKeys.delete(e.code);
            if (this.onNoteOff) {
                this.onNoteOff(info.noteName, `kb-${e.code}`);
            }
        }
    }

    getNoteName(midiNote) {
        const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const octave = Math.floor(midiNote / 12) - 1;
        const noteIndex = midiNote % 12;
        return `${notes[noteIndex]}${octave}`;
    }

    static isPresent() {
        // Simple heuristic: If it's not a touch-only device, it probably has a keyboard.
        // Also check if it's a desktop browser.
        const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
        const hasNoTouch = !window.matchMedia('(pointer: coarse)').matches;
        return hasFinePointer || hasNoTouch;
    }
}
